/**
 * The organization's word for its members as a group:
 * `organizations.terminology.alumni` (ALU-155).
 *
 * The product says "exalumnos" everywhere; universities in Rioplatense
 * Spanish say "graduados", others "egresados". The admin picks one in
 * Ajustes › Terminología, next to the institution noun. Unset keeps the
 * current wording.
 *
 * The stored value is a kind, not a word, so each language says it its own
 * way: `alumni` is exalumnos / alumni / anciens élèves, `graduates` is
 * graduados / graduates / diplômés, `egresados` is egresados / graduates /
 * diplômés. The words live in `encuentro.common.alumniNoun`.
 *
 * Only copy that names the members as a group uses it (the Informe, the
 * Resumen, the frame's "Comunidad de exalumnos"); generic words stay.
 *
 * No zod here: this module reaches client bundles. The save validation
 * lives in the server action.
 */

export const ALUMNI_KINDS = ['alumni', 'graduates', 'egresados'] as const
export type AlumniKind = (typeof ALUMNI_KINDS)[number]

/** The wording when the organization has not picked one. */
export const DEFAULT_ALUMNI_KIND: AlumniKind = 'alumni'

export function isAlumniKind(value: unknown): value is AlumniKind {
  return (ALUMNI_KINDS as readonly unknown[]).includes(value)
}

/** The organization's pick, read from its `terminology` JSON; null when unset (or unknown). */
export function readAlumniKind(terminology?: unknown): AlumniKind | null {
  if (!terminology || typeof terminology !== 'object' || Array.isArray(terminology)) return null
  const raw = (terminology as Record<string, unknown>).alumni
  const value = typeof raw === 'string' ? raw.trim() : ''
  return isAlumniKind(value) ? value : null
}
